import React, {useState, useEffect} from 'react';
import {Box, Stack, Grid, Typography, Divider, IconButton} from '@mui/material';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import ArrNumericInput from '../../../../../common/arr-numeric-input/ArrNumericInput';
import {t} from 'i18next';
import ArrTooltipWrapper from '../../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../../shared/constants';
import {useFieldData} from '../../../../../../hooks/session-search/useSessionSearch';
import {FieldTypeProps} from '../FieldTypeProps';
import './ScoreFieldType.scss';
import '../FieldTypes.scss';

export interface ScoresFieldTypeProps
  extends Omit<FieldTypeProps, 'onCriteriaChaged'> {
  onCriteriaChaged?: (
    scores: string[],
    min: number | undefined,
    max: number | undefined
  ) => void;
}

const ScoreFieldType: React.FC<ScoresFieldTypeProps> = ({
  type = 'SCORES',
  onCriteriaChaged,
  onValidated,
}) => {
  const {data} = useFieldData(type);
  const [minValue, setMinValue] = useState<number>();
  const [maxValue, setMaxValue] = useState<number>();
  const [selectedScores, setSelectedScores] = useState<string[]>([]);
  const [treeItems, setTreeItems] = useState<any[]>([]);
  const [showToast, setShowToast] = useState(false);

  const convertToTree = (items: any[]) => {
    const map: any = {};
    const roots: any[] = [];

    items.forEach((item) => {
      map[item.id] = {
        id: item.id,
        label: item.text,
        isGroup: item.isGroup,
        parent: item.parent,
        fileType: item.isGroup ? 'folder' : 'tag',
        children: [],
      };
    });

    // build the tree
    items.forEach((item) => {
      if (item.parent && item.parent !== '#') {
        map[item.parent]?.children.push(map[item.id]);
      } else {
        roots.push(map[item.id]);
      }
    });

    return roots;
  };

  // Handle tree selection
  useEffect(() => {
    if (!data?.workProductItemsDetails) return;

    const tree = convertToTree(data.workProductItemsDetails);
    setTreeItems(tree);
    setSelectedScores([]);
    setMinValue(undefined);
    setMaxValue(undefined);
    onValidated?.(false);
  }, [data]);

  const validateForm = (selected: string[], min?: number, max?: number) => {
    // User has to select at least one score
    const hasScore = selected.length > 0;

    // MIN and MAX are optional.
    // If MIN is not provided, it defaults to 70
    // If MAX is not provided, it defaults to 100 (which gets translated to '*' for some reason)
    // The provided range must be valid i.e. min <= max
    const minimum = min ?? 70;
    const maximum = max ?? 100;
    const isRangeValid = minimum <= maximum;
    if (isRangeValid) {
      setShowToast(false);
    } else {
      setShowToast(true);
    }

    const valid = hasScore && isRangeValid;
    onValidated?.(valid);
  };

  const findItemById = (items: any[], id: string): any => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children?.length) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleTreeChange = (selectedItems: string[] | null) => {
    if (!selectedItems) return;
    setSelectedScores(selectedItems);
    validateForm(selectedItems, minValue, maxValue);
    const selectedData = selectedItems
      .map((id) => findItemById(treeItems, id))
      .filter((item) => item && !item.isGroup && item.parent !== '#')
      .map((item) => item!.id);
    onCriteriaChaged?.(selectedData, minValue, maxValue);
  };

  // Handle both numeric changes
  const handleMinChange = (value?: number) => {
    setMinValue(value);
    validateForm(selectedScores, value, maxValue);
    onCriteriaChaged?.(selectedScores, value, maxValue);
  };

  const handleMaxChange = (value?: number) => {
    setMaxValue(value);
    validateForm(selectedScores, minValue, value);
    onCriteriaChaged?.(selectedScores, minValue, value);
  };

  return (
    <Grid container className="score-panel">
      {/* Left Panel */}
      <Grid size={6} className="score-panel_left-pane">
        <Stack spacing={2}>
          <Box className="score-panel-header" tabIndex={0}>
            <Typography
              className="typo-text subhead4_size right-pane_header"
              data-testid={`score-field-type-conceptual-confidence-scores`}
            >
              {t(
                'sessionSearch.queryList.workProduct.scoreDrawer.conceptualConfidenceScores'
              )}
            </Typography>
            <ArrTooltipWrapper
              trigger={
                <IconButton
                  aria-label="Info"
                  sx={{padding: 0}}
                  className="icon-info"
                >
                  <InfoOutlinedIcon fontSize="small" sx={{color: '#89A6FF'}} />
                </IconButton>
              }
            >
              {t('sessionSearch.modifiers.scoreLeftPanelToolTip')}
            </ArrTooltipWrapper>
          </Box>
          <Box>
            <Divider className="score-divider" />
          </Box>
          <Box tabIndex={0} className="score-tree-view">
            <ArrTreeView
              itemsList={treeItems}
              showTooltip={false}
              handleNoResultMessage="No Scores Found"
              allowGlobalSelect
              multipleSelection
              isShowCheckbox={true}
              setSelectedItem={handleTreeChange}
              isShowFileType={false}
              isChildrenIndendation={true}
            />
          </Box>
        </Stack>
      </Grid>
      {/* Right Panel */}
      <Grid size={6} className="score-panel_right-pane">
        <Stack spacing={2}>
          <Box className="score-panel-header" tabIndex={0}>
            <Typography
              className="typo-text subhead4_size right-pane_header"
              data-testid={`score-field-type-specify-score-range`}
            >
              {t(
                'sessionSearch.queryList.workProduct.scoreDrawer.specifyScoreRange'
              )}
            </Typography>
            <ArrTooltipWrapper
              trigger={
                <IconButton
                  aria-label="Info"
                  sx={{padding: 0}}
                  className="icon-info"
                >
                  <InfoOutlinedIcon fontSize="small" sx={{color: '#89A6FF'}} />
                </IconButton>
              }
            >
              {t('sessionSearch.modifiers.scoreRightPanelToolTip')}
            </ArrTooltipWrapper>
          </Box>

          <Box>
            <Divider className="score-divider" />
          </Box>
          <Box className="group-item field-logic">
            <ArrNumericInput
              value={minValue}
              placeholder="Enter a Minimum Score"
              onChange={handleMinChange}
              sizeVariant="large"
              min={1}
              max={100}
              minWidth="300px"
              showPercentage={false}
              supportText="Enter only numeric values from 1 to 100"
              dataTestId="min-score"
            />
          </Box>
          <Box>
            <ArrNumericInput
              value={maxValue}
              placeholder="Enter a Maximum Score"
              onChange={handleMaxChange}
              sizeVariant="large"
              min={1}
              max={100}
              minWidth="300px"
              showPercentage={false}
              dataTestId="max-score"
              supportText="Enter only numeric values from 1 to 100"
            />
          </Box>
        </Stack>
      </Grid>
      {showToast && (
        <ArrToastNotification
          message={t(
            'sessionSearch.queryList.workProduct.scoreDrawer.rangeError'
          )}
          status={[ToastType.Warning]}
          onClose={() => setShowToast(false)}
        />
      )}
    </Grid>
  );
};

export default ScoreFieldType;
